import { connectorsByIdentifier } from "../../../shared/connectors";
import { connectionTestAlwaysSkips } from "../../../shared/connectors/connectionTestWouldSkip";
import { useFormatRelativeTime } from "../../hooks/useFormatRelativeTime";

export function useConnectorStatusTooltip({
  identifier,
  configured,
  isPending,
  status,
  lastTestAt,
  lastTestMessage,
  oauthError = null,
  untestable = false,
}: {
  identifier: string;
  configured: boolean;
  isPending: boolean;
  status: "pending" | "success" | "failure" | null | undefined;
  lastTestAt: number | null;
  lastTestMessage: string | null;
  oauthError?: string | null;
  untestable?: boolean;
}): { text: string; httpStatusCode?: string } | null {
  const formatRelativeTime = useFormatRelativeTime();

  // A dead OAuth connection is the root cause of any other symptom (failed or
  // impossible tests), so its message wins outright.
  if (oauthError) return { text: oauthError };

  if (
    connectionTestAlwaysSkips(
      connectorsByIdentifier[identifier]?.connectionTest
    )
  ) {
    return { text: "Connection tests aren’t supported" };
  }

  if (untestable) return { text: "Configure a connection test" };

  if (status === "failure" && lastTestMessage) {
    if (/\b(?:timed out|timeout)\b/i.test(lastTestMessage))
      return { text: "Connection test timed out" };
    const httpStatus = lastTestMessage.match(/^Got status (\d{3})\b/);
    if (httpStatus)
      return { text: "Test failed", httpStatusCode: httpStatus[1] };
    return { text: lastTestMessage };
  }

  if (configured && !isPending && lastTestAt && status) {
    return {
      text: `Test ${status === "success" ? "passed" : "failed"} ${formatRelativeTime(lastTestAt)}`,
    };
  }

  return null;
}
